/*
  <back-to-top> holds the round button rendered by sections/footer.liquid.

  The button appears once the shopper is about a screen and a half down the
  page. That point is found without a scroll listener: a one pixel marker sits
  at the very top of the document and is observed with a root margin that
  reaches one and a half viewports above the screen, so the marker stops
  intersecting exactly when the page has been scrolled that far.

  Pressing the button scrolls to the top and moves focus to the main content,
  the same place the skip link leads to.
*/
if (!customElements.get('back-to-top')) {
  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-back-to-top]');
      if (!this.button || !('IntersectionObserver' in window)) return;

      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      this.marker = document.createElement('span');
      this.marker.className = 'back-to-top__marker';
      this.marker.setAttribute('aria-hidden', 'true');
      document.body.prepend(this.marker);

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          this.classList.toggle('is-visible', !entry.isIntersecting);
        },
        { rootMargin: '150% 0px 0px 0px' }
      );
      this.observer.observe(this.marker);
    }

    disconnectedCallback() {
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
      if (this.marker) this.marker.remove();
    }

    onClick() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });

      const target = document.getElementById(this.dataset.target || 'main-content');
      if (!target) return;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      /* The scroll above is already under way; focusing must not jump the page. */
      target.focus({ preventScroll: true });
    }
  }

  customElements.define('back-to-top', BackToTop);
}
